import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Loader2, Download, LogOut, Receipt, Percent, Wallet } from "lucide-react";
import logo from "@/assets/logo.png";
import OrderFormButton from "@/components/OrderFormButton";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ecole-manager-token";
const mApi = axios.create({ baseURL: BASE });
mApi.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
const euros = (c) => (Number(c || 0) / 100).toFixed(2);
const STATUS = { pending_payment: "En attente de paiement", paid: "Payée", delivered: "Remise", cancelled: "Annulée" };

export default function EcoleResponsable() {
  const [data, setData] = useState(null); // summary
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

  const load = async () => {
    try { const r = await mApi.get(`/ecoles/manager/summary`); setData(r.data); setAuthed(true); }
    catch { localStorage.removeItem(TOKEN_KEY); setAuthed(false); setData(null); }
  };
  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, []);

  const validatePayment = async (orderId) => {
    try { await mApi.put(`/ecoles/manager/orders/${orderId}/validate-payment`); toast.success("Paiement validé"); await load(); }
    catch { toast.error("Validation impossible"); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); setData(null); };

  const exportExcel = async () => {
    try {
      const res = await mApi.get(`/ecoles/manager/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "commandes_ecole.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10" data-testid="ecole-responsable-page">
      <div className="mb-8 flex items-center gap-3">
        <img src={logo} alt="" className="h-12 w-12 object-contain" />
        <p className="font-serif text-3xl font-semibold text-bark">Espace responsable d'école</p>
      </div>

      {!authed || !data ? (
        <ManagerLogin onAuthed={load} />
      ) : (
        <div className="space-y-6" data-testid="manager-dashboard">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 font-serif text-2xl font-semibold text-bark"><GraduationCap size={22} className="text-amber-deep" /> {data.school.name}</p>
              <p className="text-sm text-bark-soft">{data.school.city} · Commission {data.school.commission_rate}%</p>
            </div>
            <button data-testid="manager-logout" onClick={logout} className="flex items-center gap-1.5 text-sm text-bark-soft hover:text-terra"><LogOut size={14} /> Déconnexion</button>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Stat icon={Receipt} label="Commandes" value={data.order_count} />
            <Stat icon={Wallet} label="Total des ventes" value={`${euros(data.total_cents)} €`} />
            <Stat icon={Percent} label="Commission à reverser" value={`${euros(data.commission_cents)} €`} highlight />
          </div>

          <div className="rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="font-serif text-lg font-semibold text-bark">Commandes</p>
              <div className="flex flex-wrap gap-2">
              <OrderFormButton client={mApi} path="/ecoles/manager/bon-de-commande" testid="manager-order-form-pdf" />
              <button data-testid="manager-export" onClick={exportExcel} className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark hover:border-amber-deep hover:text-amber-deep"><Download size={15} /> Exporter (Excel)</button>
              </div>
            </div>
            {data.orders.length === 0 ? (
              <p className="py-6 text-center text-sm text-bark-soft">Aucune commande pour l'instant.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead><tr className="border-b border-amber-900/10 text-xs uppercase tracking-wider text-stone-mute">
                    <th className="py-3 pr-4">Élève</th><th className="py-3 pr-4">Classe</th><th className="py-3 pr-4">Parent</th><th className="py-3 pr-4">Total</th><th className="py-3 pr-4">Commission</th><th className="py-3">Statut</th>
                  </tr></thead>
                  <tbody className="divide-y divide-amber-900/10">
                    {data.orders.map((o) => (
                      <tr key={o.order_id} data-testid={`manager-order-${o.order_id}`}>
                        <td className="py-2.5 pr-4 font-medium text-bark">{o.student_first} {o.student_last}</td>
                        <td className="py-2.5 pr-4 text-bark-soft">{o.student_class || "—"}</td>
                        <td className="py-2.5 pr-4 text-bark-soft">{o.parent_name}{o.buyer_type === "proche" && <span data-testid={`proche-badge-${o.order_id}`} className="ml-1.5 rounded-full bg-terra/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-terra">Proche</span>}<br /><span className="text-xs">{o.parent_email}</span></td>
                        <td className="py-2.5 pr-4 text-bark">{euros(o.subtotal_cents)} €</td>
                        <td className="py-2.5 pr-4 font-semibold text-amber-deep">{euros(o.commission_cents)} €</td>
                        <td className="py-2.5 text-bark-soft">
                          {STATUS[o.status] || o.status}
                          {o.status === "pending_payment" && (
                            <button data-testid={`manager-validate-${o.order_id}`} onClick={() => validatePayment(o.order_id)} className="mt-1 block rounded-full bg-sage px-3 py-1 text-xs font-semibold text-white hover:bg-sage/90">Valider le paiement</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {(data.by_class || []).length > 0 && (
            <div className="rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm" data-testid="manager-by-class">
              <p className="mb-3 font-serif text-lg font-semibold text-bark">Récapitulatif par classe <span className="text-sm font-normal text-bark-soft">(pour la remise groupée)</span></p>
              <div className="space-y-4">
                {data.by_class.map((g) => (
                  <div key={g.klass} data-testid={`class-recap-${g.klass}`} className="rounded-xl border border-amber-800/10 p-4">
                    <p className="font-semibold text-bark">{g.klass} <span className="text-sm font-normal text-bark-soft">· {g.order_count} commande(s) · {euros(g.total_cents)} €</span></p>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-stone-mute">Total produits</p>
                        <ul className="mt-1 space-y-1 text-sm">
                          {g.products.map((p, i) => (
                            <li key={i} className="flex justify-between gap-2"><span className="text-bark">{p.name}{p.weight ? ` - ${p.weight}` : ""}</span><span className="font-semibold text-amber-deep">×{p.qty}</span></li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-stone-mute">Par élève</p>
                        <ul className="mt-1 space-y-1 text-sm">
                          {g.students.map((st, i) => (
                            <li key={i} className="text-bark-soft"><span className="font-medium text-bark">{st.name}</span> — {st.items.map((it) => `${it.name}${it.weight ? " - " + it.weight : ""} ×${it.qty}`).join(", ")}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-2xl border p-5 ${highlight ? "border-amber-deep/30 bg-amber-deep/5" : "border-amber-800/15 bg-white"}`}>
      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${highlight ? "bg-amber-deep/15 text-amber-deep" : "bg-sand text-bark-soft"}`}><Icon size={18} /></span>
      <p className="mt-3 text-xs uppercase tracking-wider text-stone-mute">{label}</p>
      <p className="font-serif text-2xl font-semibold text-bark">{value}</p>
    </div>
  );
}

function ManagerLogin({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await mApi.post(`/auth/login`, { email: email.trim(), password });
      if (data.requires_2fa || data.role !== "school_manager") throw { response: { data: { detail: "Identifiants de responsable incorrects." } } };
      localStorage.setItem(TOKEN_KEY, data.access_token);
      await onAuthed();
    } catch (e) { toast.error(e.response?.data?.detail || "Connexion impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm" data-testid="manager-login">
      <p className="mb-4 text-sm text-bark-soft">Connectez-vous avec les identifiants fournis par la miellerie.</p>
      <div className="space-y-3">
        <input data-testid="manager-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" className="w-full rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
        <input data-testid="manager-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" className="w-full rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
        <button data-testid="manager-submit" onClick={submit} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-3 text-sm font-semibold text-white hover:bg-amber-deep/90 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} Se connecter
        </button>
      </div>
    </div>
  );
}
